/**
 * AdminLayout — completely separate shell from the user-side AppLayout.
 * Has its own sidebar, redirects non-admins back to /dashboard.
 * Mounted only at /admin/* routes so the user side stays 100% untouched.
 */
import { useEffect, useState } from "react";
import { Outlet, useLocation, NavLink, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, FileQuestion, Stethoscope,
  BookOpen, Megaphone, ArrowLeft, BadgePercent, LifeBuoy, Sparkles, DollarSign, Database, KeyRound, ShieldQuestion, Mail, Filter, SlidersHorizontal, FileText, TrendingUp, Gift, ShoppingCart,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/users", icon: Users, label: "Users", testid: "admin-nav-users" },
  { to: "/admin/missed-trials", icon: Gift, label: "Missed trials", testid: "admin-nav-missed-trials" },
  { to: "/admin/mcqs", icon: FileQuestion, label: "MCQs", testid: "admin-nav-mcqs" },
  { to: "/admin/scenarios", icon: Stethoscope, label: "Scenarios", testid: "admin-nav-scenarios" },
  { to: "/admin/topics", icon: BookOpen, label: "Learn topics", testid: "admin-nav-topics" },
  { to: "/admin/lessons-review", icon: Sparkles, label: "Lesson drafts", testid: "admin-nav-lessons-review" },
  { to: "/admin/plans", icon: DollarSign, label: "Billing & coupons", testid: "admin-nav-plans" },
  { to: "/admin/announcements", icon: Megaphone, label: "Announcements", testid: "admin-nav-announcements" },
  { to: "/admin/promo-codes", icon: BadgePercent, label: "Promo codes", testid: "admin-nav-promos" },
  { to: "/admin/support", icon: LifeBuoy, label: "Support", testid: "admin-nav-support", badgeKey: "admin_unread" },
  { to: "/admin/question-audit", icon: ShieldQuestion, label: "Question audit", testid: "admin-nav-question-audit" },
  { to: "/admin/question-repair", icon: FileQuestion, label: "Question repair", testid: "admin-nav-question-repair" },
  { to: "/admin/leads", icon: Users, label: "Leads", testid: "admin-nav-leads" },
  { to: "/admin/marketing", icon: Mail, label: "Email marketing", testid: "admin-nav-marketing" },
  { to: "/admin/abandoned-checkouts", icon: ShoppingCart, label: "Abandoned checkouts", testid: "admin-nav-abandoned-checkouts" },
  { to: "/admin/funnel", icon: Filter, label: "Activation funnel", testid: "admin-nav-funnel" },
  { to: "/admin/tracking", icon: TrendingUp, label: "Tracking", testid: "admin-nav-tracking" },
  { to: "/admin/content-sync", icon: Database, label: "Content sync", testid: "admin-nav-content-sync" },
  { to: "/admin/settings", icon: KeyRound, label: "Settings & keys", testid: "admin-nav-settings" },
  { to: "/admin/site-settings", icon: SlidersHorizontal, label: "Site settings", testid: "admin-nav-site-settings" },
  { to: "/admin/site-content", icon: FileText, label: "Site content", testid: "admin-nav-site-content" },
];

const AdminLayout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [supportStats, setSupportStats] = useState({ admin_unread: 0 });

  // Poll support stats every 30s while in admin
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const fetchStats = async () => {
      try {
        const r = await axios.get(`${API}/admin/support/stats`);
        setSupportStats(r.data || {});
      } catch { /* silent */ }
    };
    fetchStats();
    const id = setInterval(fetchStats, 30000);
    return () => clearInterval(id);
  }, [user, location.pathname]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        data-testid="admin-sidebar"
        className="w-64 border-r border-border bg-card/60 flex flex-col py-6 px-4 sticky top-0 h-screen"
      >
        <div className="px-2 mb-6">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Admin Portal</p>
          <h1 className="text-lg font-bold mt-1">PassADC</h1>
        </div>

        <nav className="flex flex-col gap-1">
          {navItems.map(({ to, icon: Icon, label, end, testid, badgeKey }) => {
            const badge = badgeKey ? supportStats[badgeKey] : 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{label}</span>
                {badge > 0 && (
                  <span data-testid={`${testid}-badge`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground min-w-[1.25rem] text-center">
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <NavLink
          to="/dashboard"
          data-testid="admin-back-to-user-app"
          className="mt-auto flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to student app</span>
        </NavLink>
      </aside>

      <main className="flex-1 overflow-x-auto">
        <div className="p-6 md:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
